import React, { useContext, memo, useRef, useCallback, useMemo } from "react";
import WidgetCard from "./WidgetCard";
import { TiBatteryFull } from "react-icons/ti";
import EChartsReact from "echarts-for-react";
import AppContext from "context/Context";
import { getColor } from "helpers/utils";
import { Row, Col, Badge } from "react-bootstrap";
import { sortBatteries, getTrackers } from "services/stillageDashboard";
import { useDispatch } from 'react-redux';
import { openModal } from "slices/stillageWidgetModalSlice";
import PropTypes from 'prop-types';

const Batteries = ({ data, loading }) => {
    const { config: { isDark } } = useContext(AppContext);
    const dispatch = useDispatch();
    const batteryRef = useRef(null);
    const { full, low, empty } = sortBatteries(data?.trackerData);
    const { tracked, notTracked } = getTrackers(data?.tableData);

    const highlightSlice = useCallback((index) => {
        const instance = batteryRef?.current?.getEchartsInstance?.();
        if (instance) {
            instance.dispatchAction({
                type: 'highlight',
                seriesIndex: 0,
                dataIndex: index
            });
        }
    }, []);

    const downplaySlice = useCallback((index) => {
        const instance = batteryRef?.current?.getEchartsInstance?.();
        if (instance) {
            instance.dispatchAction({
                type: 'downplay',
                seriesIndex: 0,
                dataIndex: index
            });
        }
    }, []);

    const PillBadge = ({ bg, idx, data, title }) => (
        <Badge
            pill
            bg={bg}
            className="px-3 text-wrap"
            onMouseEnter={() => highlightSlice(idx)}
            onMouseLeave={() => downplaySlice(idx)}
            onClick={() => dispatch(openModal({ title, data: data || [] }))}>
            <small>{title}</small>
        </Badge>
    );
    PillBadge.propTypes = {
        bg: PropTypes.string,
        idx: PropTypes.number,
        data: PropTypes.array,
        title: PropTypes.string
    };

    const chartOptions = useMemo(() => ({
        legend: { show: false },
        title: {
            text: 'Battery Levels',
            textStyle: {
                fontSize: 12,
                fontWeight: 'normal'
            }
        },
        series: [
            {
                type: 'pie',
                startAngle: 180,
                endAngle: 360,
                label: {
                    show: false,
                    position: 'center',
                },
                emphasis: {
                    label: {
                        show: true,
                        fontWeight: 'bold',
                        formatter: '{c}',
                    }
                },
                pointer: {
                    show: false
                },
                data: [
                    {
                        name: 'Full',
                        value: full?.length,
                        itemStyle: {
                            color: getColor('success')
                        }
                    },
                    {
                        name: 'Low',
                        value: low?.length,
                        itemStyle: {
                            color: getColor('warning')
                        }
                    },
                    {
                        name: 'Empty',
                        value: empty?.length,
                        itemStyle: {
                            color: getColor('danger')
                        }
                    }
                ],
                radius: ['70%', '100%'],
                center: ['50%', '75%'],
            }
        ],
        darkMode: isDark
    }), [full.length, low.length, empty.length, isDark]);

    const trackerOptions = useMemo(() => ({
        title: {
            title: 'Trackers Fitted'
        },
        legend: {
            show: true,
            textStyle: {
                color: isDark ? '#cececeff' : '#000'
            },
            top: 0,
            itemWidth: 15,
            align: 'auto',
            orient: 'horizontal'
        },
        series: [
            {
                type: 'pie',
                label: {
                    show: false,
                    position: 'center',
                },
                emphasis: {
                    label: {
                        show: true,
                        fontWeight: 'bold',
                        formatter: '{c}',
                    }
                },
                pointer: {
                    show: false
                },
                data: [
                    {
                        name: 'Tracked',
                        value: tracked?.length,
                        itemStyle: {
                            color: getColor('success')
                        }
                    },
                    {
                        name: 'Not Tracked',
                        value: notTracked?.length,
                        itemStyle: {
                            color: getColor('danger')
                        }
                    },
                ],
                radius: ['40%', '60%'],
                center: ['50%', '60%'],
            }
        ],
        darkMode: isDark
    }), [tracked.length, notTracked.length, isDark]);

    const pills = [
        { bg: 'success', data: full || [], title: '>50%' },
        { bg: 'warning', data: low || [], title: '<50%' },
        { bg: 'danger', data: empty || [], title: '0%' },
    ]

    return (
        <WidgetCard title="Tracker Status" icon={<TiBatteryFull />} loading={loading}>
            <Row className="h-100">
                <Col sm={6}>
                    <EChartsReact
                        ref={batteryRef}
                        option={chartOptions}
                        style={{ height: '6rem' }}
                        onEvents={{
                            'click': e => dispatch(openModal({ title: e.data.name, data: pills[e.dataIndex]?.data }))
                        }}
                    />
                    <div className="d-flex justify-content-evenly">
                        {pills.map((pill, idx) =>
                            <PillBadge
                                key={pill.title + idx}
                                bg={pill.bg}
                                idx={idx}
                                title={pill.title}
                                data={pill.data}
                            />)}
                    </div>
                </Col>
                <Col sm={6}>
                    <EChartsReact
                        onEvents={{
                            'click': e => dispatch(openModal({ title: 'Trackers', data: e.name == 'Tracked' ? tracked : notTracked }))
                        }}
                        option={trackerOptions}
                        style={{ height: '6rem' }}
                    />
                </Col>
            </Row>
        </WidgetCard>
    );
}

Batteries.propTypes = {
    data: PropTypes.array,
    loading: PropTypes.bool
};

export default memo(Batteries);